'use client';

import { motion } from 'motion/react';
import Image from '@/components/Image';
import { FaChrome } from 'react-icons/fa6';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

/* ── Mini Scratch block visuals ── */
function ScratchBlocks({ type }: { type: 'move' | 'clone' | 'variable' | 'idea' }) {
  if (type === 'move') {
    return (
      <div className="flex flex-col gap-1">
        <div className="rounded bg-[#FFBF00] px-2.5 py-1 text-[10px] font-bold text-white shadow-sm">when &#9873; clicked</div>
        <div className="rounded bg-[#4C97FF] px-2.5 py-1 text-[10px] font-bold text-white shadow-sm opacity-50">move 10 steps</div>
        <div className="mt-1 rounded border-2 border-dashed border-[#4C97FF]/40 bg-[#4C97FF]/5 px-2 py-2 text-center text-[9px] text-[#4C97FF]">
          &#8593; should be inside forever loop
        </div>
      </div>
    );
  }
  if (type === 'clone') {
    return (
      <div className="flex flex-col gap-1">
        <div className="rounded bg-[#FFAB19] px-2.5 py-1 text-[10px] font-bold text-white shadow-sm">forever</div>
        <div className="ml-3 rounded bg-[#9966FF] px-2.5 py-1 text-[10px] font-bold text-white shadow-sm">create clone of myself</div>
        <div className="mt-1 flex items-center gap-1">
          <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-400" />
          <span className="text-[9px] text-red-400">infinite clones!</span>
        </div>
      </div>
    );
  }
  if (type === 'variable') {
    return (
      <div className="flex flex-col gap-1">
        <div className="rounded bg-[#FF8C1A] px-2.5 py-1 text-[10px] font-bold text-white shadow-sm">set score to 0</div>
        <div className="rounded bg-[#CF63CF] px-2.5 py-1 text-[10px] font-bold text-white shadow-sm">change score by 1</div>
        <div className="mt-1 rounded border border-[#CF63CF]/30 bg-[#CF63CF]/5 px-2 py-1 text-center text-[9px] text-[#CF63CF]">
          &ldquo;for this sprite only&rdquo; &#10060;
        </div>
      </div>
    );
  }
  // idea
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <div className="flex h-5 w-5 items-center justify-center rounded bg-[#FF8C1A]/20 text-[9px]">&#127918;</div>
        <span className="text-[10px] font-semibold text-[#4A5568]">Platformer</span>
      </div>
      <div className="flex flex-wrap gap-1">
        <div className="rounded bg-[#4C97FF]/15 px-1.5 py-0.5 text-[9px] text-[#4C97FF]">gravity?</div>
        <div className="rounded bg-[#9966FF]/15 px-1.5 py-0.5 text-[9px] text-[#9966FF]">collision?</div>
        <div className="rounded bg-[#CF63CF]/15 px-1.5 py-0.5 text-[9px] text-[#CF63CF]">levels?</div>
      </div>
    </div>
  );
}

/* ── Pain points kids hit on Scratch ── */
const PAIN_POINTS: {
  problem: string;
  context: string;
  pengiResponse: string;
  color: string;
  blockType: 'move' | 'clone' | 'variable' | 'idea';
}[] = [
  {
    problem: '"My sprite won\'t move!"',
    context: 'Forgot to put the move block inside a loop.',
    pengiResponse: '"I see your move block is outside the loop. What happens if we drag it inside the forever block?"',
    color: '#4C97FF',
    blockType: 'move',
  },
  {
    problem: '"My game keeps crashing!"',
    context: 'Infinite clone spawning, no stop condition.',
    pengiResponse: '"Your clone count is growing fast. What condition should stop creating new clones?"',
    color: '#9966FF',
    blockType: 'clone',
  },
  {
    problem: '"The score isn\'t working!"',
    context: 'Variable scope: "for this sprite only" vs "for all sprites".',
    pengiResponse: '"Your score variable is set to \\"for this sprite only\\". What happens when another sprite tries to read it?"',
    color: '#CF63CF',
    blockType: 'variable',
  },
  {
    problem: '"I don\'t know how to make this..."',
    context: 'Wants to build a platformer but has no idea where to start.',
    pengiResponse: '"Let\'s start simple. What should happen to your character when they\'re not standing on a platform?"',
    color: '#FF8C1A',
    blockType: 'idea',
  },
];

/* ── Step-by-step onboarding flow ── */
const ONBOARDING_STEPS = [
  {
    step: '1',
    title: 'Install & sign in',
    subtitle: '30 seconds.',
    body: 'Add the Pengi extension from the Chrome Web Store and sign in with Google or email. Done.',
    detail: 'Works on Chrome, Edge, Brave. Parents can create managed student accounts.',
  },
  {
    step: '2',
    title: 'Open Scratch',
    subtitle: 'Pengi appears automatically.',
    body: 'Open any project on scratch.mit.edu. A small Pengi button appears in the editor — click it when you need help.',
    detail: 'Pengi reads your blocks, sprites, and project state in real time. No copy-pasting needed.',
  },
  {
    step: '3',
    title: 'Ask anything, anytime',
    subtitle: 'Voice or text. 24/7.',
    body: '"Why isn\'t my sprite moving?" "How do I add gravity?" "What\'s wrong with my loop?" — just ask out loud or type.',
    detail: 'Pengi responds with guiding questions, never with answers. Your child learns by discovering, not copying.',
  },
];

/* ── AI + courses synergy ── */
const SYNERGY_POINTS = [
  {
    title: 'Between classes',
    description: 'Your child learns concepts in their coding course. When they practice at home and get stuck, Pengi bridges the gap until the next class.',
    icon: '🕐',
  },
  {
    title: 'Beyond the curriculum',
    description: 'Courses follow a fixed path. When your child wants to build something off-script — a game, an animation, a story — Pengi guides them through uncharted territory.',
    icon: '🚀',
  },
  {
    title: 'Building debugging muscle',
    description: 'Real programmers spend most of their time debugging. Pengi teaches this skill from day one by asking "what do you think went wrong?" instead of just fixing it.',
    icon: '🔧',
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

interface ScratchPageClientProps {
  chromeStoreUrl: string;
}

export default function ScratchPageClient({ chromeStoreUrl }: ScratchPageClientProps) {
  return (
    <>
      {/* ══════ PAIN POINTS — "Sound familiar?" ══════ */}
      <section
        className="py-14 md:py-20"
        style={{
          background:
            'linear-gradient(180deg, #FAFCFF 0%, #F4F7FB 30%, #F4F7FB 70%, #FAFCFF 100%)',
        }}
      >
        <div className="mx-auto max-w-[1000px] px-4 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">
                Real Problems
              </span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>

            <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <h2 className="emphasize-text max-w-[480px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[40px]">
                Sound familiar?
              </h2>
              <p className="display-text max-w-[320px] text-sm leading-[1.8] text-[#4A5A72] md:text-right">
                These are the moments kids close the laptop and give up. Pengi turns them into breakthroughs.
              </p>
            </div>
          </motion.div>

          {/* Swiper carousel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
          >
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              breakpoints={{
                0: { slidesPerView: 1.08 },
                640: { slidesPerView: 1.5 },
                900: { slidesPerView: 2 },
              }}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3800, disableOnInteraction: false, pauseOnMouseEnter: true }}
              loop
              style={
                {
                  '--swiper-pagination-color': '#FF8C1A',
                  '--swiper-pagination-bullet-inactive-color': '#D1D9E6',
                  '--swiper-pagination-bullet-inactive-opacity': '1',
                  paddingBottom: '44px',
                } as React.CSSProperties
              }
            >
              {PAIN_POINTS.map((item) => (
                <SwiperSlide key={item.problem}>
                  <div
                    className="group h-full rounded-[20px] border border-[#E3E8F2] bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)]"
                  >
                    <div className="flex gap-4">
                      {/* Left: Scratch block visual */}
                      <div className="hidden shrink-0 sm:block">
                        <div className="w-[130px] overflow-hidden rounded-xl bg-[#F8F9FC] p-3">
                          <ScratchBlocks type={item.blockType} />
                        </div>
                      </div>

                      {/* Right: text content */}
                      <div className="min-w-0 flex-1">
                        <p className="emphasize-text text-base text-[#0D1929]">{item.problem}</p>
                        <p className="display-text mt-1 text-sm leading-[1.6] text-[#8A96A8]">{item.context}</p>

                        {/* Pengi's response */}
                        <div className="mt-3 rounded-xl bg-[#F0FBE8] p-3 transition-all duration-200 group-hover:bg-[#E5F7DA]">
                          <div className="mb-1 flex items-center gap-1.5">
                            <Image src="/assets/landing-logo.png" alt="Pengi" width={16} height={16} optimizedWidth={32} />
                            <span className="display-text-semi text-[11px] text-[#277A1C]">Pengi:</span>
                          </div>
                          <p className="display-text text-[13px] leading-[1.6] text-[#277A1C] italic">
                            {item.pengiResponse}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </section>

      {/* ══════ STATS + BEFORE/AFTER ══════ */}
      <section className="bg-[#FAFCFF] py-14 md:py-20">
        <div className="mx-auto max-w-[1000px] px-4 md:px-8">
          {/* Stats row */}
          <motion.div
            className="mb-16 grid gap-5 sm:grid-cols-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
          >
            {[
              { number: '3x', label: 'more projects completed', sub: 'vs learning alone' },
              { number: '5 min', label: 'average time to unstuck', sub: 'down from 40+ min' },
              { number: '87%', label: 'of kids keep coding', sub: 'instead of giving up' },
            ].map((stat) => (
              <motion.div
                key={stat.label}
                variants={fadeInUp}
                transition={{ duration: 0.4 }}
                className="rounded-[20px] border border-[#E3E8F2] bg-white p-6 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(0,0,0,0.04)]"
              >
                <p className="emphasize-text text-[36px] tracking-[-0.03em] text-[#FF8C1A] md:text-[44px]">{stat.number}</p>
                <p className="emphasize-text mt-1 text-sm text-[#0D1929]">{stat.label}</p>
                <p className="display-text mt-1 text-xs text-[#8A96A8]">{stat.sub}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Before / After comparison */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">
                The Difference
              </span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>
            <h2 className="emphasize-text mb-10 max-w-[500px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[36px]">
              Without Pengi vs. with Pengi
            </h2>
          </motion.div>

          <motion.div
            className="grid gap-5 md:grid-cols-2"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
            variants={staggerContainer}
          >
            {/* WITHOUT */}
            <motion.div
              variants={fadeInUp}
              transition={{ duration: 0.4 }}
              className="rounded-[20px] border border-[#E3E8F2] bg-white p-7"
            >
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#FEE2E2] px-3 py-1">
                <div className="h-2 w-2 rounded-full bg-[#EF4444]" />
                <span className="display-text-semi text-xs text-[#B91C1C]">Without Pengi</span>
              </div>

              <div className="space-y-4">
                {[
                  { label: 'Stuck on a bug', bar: 85, time: '40+ min googling, still lost' },
                  { label: 'Asks parent for help', bar: 70, time: 'Parent doesn\'t know Scratch' },
                  { label: 'Frustration builds', bar: 90, time: 'Closes laptop, gives up' },
                  { label: 'Next session', bar: 30, time: 'Doesn\'t want to come back' },
                ].map((row) => (
                  <div key={row.label}>
                    <div className="flex items-baseline justify-between">
                      <span className="display-text-semi text-[13px] text-[#0D1929]">{row.label}</span>
                      <span className="display-text text-[11px] text-[#8A96A8]">{row.time}</span>
                    </div>
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-[#F4F7FB]">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#FCA5A5] to-[#EF4444]"
                        style={{ width: `${row.bar}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-xl bg-[#FEF2F2] p-4 text-center">
                <p className="emphasize-text text-[13px] text-[#B91C1C]">Result: kid stops coding within weeks</p>
              </div>
            </motion.div>

            {/* WITH */}
            <motion.div
              variants={fadeInUp}
              transition={{ duration: 0.4 }}
              className="rounded-[20px] border border-[#C5EDB8] bg-white p-7 shadow-[0_4px_24px_rgba(66,197,45,0.06)]"
            >
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#F0FBE8] px-3 py-1">
                <div className="h-2 w-2 rounded-full bg-[#42C52D]" />
                <span className="display-text-semi text-xs text-[#277A1C]">With Pengi</span>
              </div>

              <div className="space-y-4">
                {[
                  { label: 'Stuck on a bug', bar: 20, time: 'Clicks Pengi, asks for help' },
                  { label: 'Guided to solution', bar: 85, time: '5 min with Socratic questions' },
                  { label: 'Confidence grows', bar: 90, time: '"I figured it out myself!"' },
                  { label: 'Next session', bar: 95, time: 'Can\'t wait to build more' },
                ].map((row) => (
                  <div key={row.label}>
                    <div className="flex items-baseline justify-between">
                      <span className="display-text-semi text-[13px] text-[#0D1929]">{row.label}</span>
                      <span className="display-text text-[11px] text-[#8A96A8]">{row.time}</span>
                    </div>
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-[#F4F7FB]">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#86EFAC] to-[#42C52D]"
                        style={{ width: `${row.bar}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-xl bg-[#F0FBE8] p-4 text-center">
                <p className="emphasize-text text-[13px] text-[#277A1C]">Result: kid builds projects independently</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══════ GETTING STARTED — Onboarding Flow ══════ */}
      <section className="bg-[#FAFCFF] py-14 md:py-20">
        <div className="mx-auto max-w-[900px] px-4 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">
                Get Started
              </span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>
            <h2 className="emphasize-text mb-12 max-w-[500px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[40px]">
              From install to &ldquo;aha!&rdquo;
              <br />
              in 30 seconds
            </h2>
          </motion.div>

          <motion.div
            className="relative"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
          >
            {/* Vertical connector line */}
            <div className="absolute left-[27px] top-[48px] hidden h-[calc(100%-96px)] w-0.5 md:block"
              style={{
                background: 'linear-gradient(to bottom, #FF8C1A, #FF8C1A 60%, transparent)',
              }}
            />

            <div className="space-y-5">
              {ONBOARDING_STEPS.map((item) => (
                <motion.div
                  key={item.step}
                  variants={fadeInUp}
                  transition={{ duration: 0.4 }}
                  className="flex gap-5 md:gap-6"
                >
                  {/* Step number */}
                  <div className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-xl shadow-md"
                    style={{
                      background: 'linear-gradient(135deg, #FF8C1A 0%, #E8740C 100%)',
                      boxShadow: '0 4px 16px rgba(255,140,26,0.25)',
                    }}
                  >
                    <span className="font-extrabold text-white">{item.step}</span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 rounded-[20px] border border-[#E3E8F2] bg-white p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#FFD6A0] hover:shadow-[0_8px_28px_rgba(255,140,26,0.06)]">
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <h3 className="emphasize-text text-base text-[#0D1929]">{item.title}</h3>
                      <span className="display-text-semi text-xs text-[#FF8C1A]">{item.subtitle}</span>
                    </div>
                    <p className="display-text mt-2 text-sm leading-[1.7] text-[#4A5A72]">{item.body}</p>
                    <p className="display-text mt-2 text-xs text-[#B0B8C4]">{item.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA after steps */}
          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <a
              href={chromeStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="display-text-bold group inline-flex items-center gap-2.5 rounded-full bg-[#FF8C1A] px-8 py-3.5 text-base text-white shadow-[0_4px_24px_rgba(255,140,26,0.3)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#E87A0C] hover:shadow-[0_8px_32px_rgba(255,140,26,0.4)]"
            >
              <FaChrome className="h-5 w-5 transition-transform duration-300 group-hover:rotate-180" />
              Get Started
            </a>
          </motion.div>
        </div>
      </section>

      {/* ══════ AI + REAL COURSES — Synergy ══════ */}
      <section
        className="py-14 md:py-20"
        style={{
          background:
            'linear-gradient(180deg, #FAFCFF 0%, #F4F7FB 30%, #F4F7FB 70%, #FAFCFF 100%)',
        }}
      >
        <div className="mx-auto max-w-[1000px] px-4 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">
                Works With Your Classes
              </span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>

            <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <h2 className="emphasize-text max-w-[520px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[40px]">
                &ldquo;But my kid already
                <br />
                takes a Scratch class&rdquo;
              </h2>
              <p className="display-text max-w-[280px] text-sm leading-[1.8] text-[#4A5A72] md:text-right">
                Great. Pengi covers the 95% of practice time when the teacher isn&apos;t there.
              </p>
            </div>
          </motion.div>

          <motion.div
            className="grid gap-5 md:grid-cols-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
          >
            {SYNERGY_POINTS.map((item) => (
              <motion.div
                key={item.title}
                variants={fadeInUp}
                transition={{ duration: 0.4 }}
                className="group rounded-[20px] border border-[#E3E8F2] bg-white p-7 transition-all duration-200 hover:-translate-y-1 hover:border-[#C5EDB8] hover:shadow-[0_8px_32px_rgba(66,197,45,0.08)]"
              >
                <div className="mb-4 text-2xl">{item.icon}</div>
                <h3 className="emphasize-text mb-3 text-base text-[#0D1929]">{item.title}</h3>
                <p className="display-text text-sm leading-[1.7] text-[#566070]">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Accent card */}
          <motion.div
            className="mt-8 rounded-[20px] p-7 text-center"
            style={{
              background: 'linear-gradient(135deg, #FFF6ED 0%, #F0FBE8 100%)',
              border: '1px solid #E8DFD4',
            }}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className="emphasize-text text-base text-[#0D1929]">
              Think of Pengi as a patient teaching assistant that&apos;s always on duty —
            </p>
            <p className="display-text mt-1.5 text-sm text-[#6B7A90]">
              at 10pm, on weekends, during school breaks, and whenever curiosity strikes.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}
